import React from "react";
import Alert from "../Alert";
import InlineLinkButton from "./InlineLinkButton";
import { SESSION_LIFETIME_HOURS } from "../../../shared/issuance/config";

/**
 * The "Need to Step Away?" line : one link that downloads the
 * Configuration File, plus the disclosure that has to travel with it.
 *
 * ONE component for BOTH screens that offer this (Step 3's validation
 * wait and Step 4's signing wait). They are the same promise made at
 * two moments, and they were briefly two hand-written copies with
 * identical wording, identical timeout handling and identical class
 * strings - the drift shape useCopyFeedback and this file's sibling
 * poll-cooldown notice were both extracted to prevent.
 *
 * The disclosure is the reason this is a component rather than a bare
 * hook call : the file carries the ACME account private key, the SSL
 * Certificate private key and the External Account Binding
 * credentials, so the treat-like-a-password sentence is not decoration
 * a caller may forget - it ships with the control. The lifetime is
 * interpolated from SESSION_LIFETIME_HOURS so the disclosed window can
 * never drift from what the session store enforces.
 *
 * Rendered as a LINK rather than a button on purpose : both screens
 * already carry a centered primary action row, and a third button
 * would compete with it.
 *
 * The FAILURE Alert is deliberately NOT rendered here - see
 * ConfigFileSaveError below for why the caller places it.
 *
 * @param {boolean} saving - in-flight flag from useConfigFileSave
 * @param {function} save  - the save action from useConfigFileSave
 */
function StepAwaySave({ saving, save }) {
  // ONE flow child, not a bare fragment : Step 4 renders this inside a
  // `space-y-4` wrapper, which gives every child but the last a 1rem
  // bottom margin. As two loose siblings the link line would take that
  // 1rem, which collapses with the disclosure's `mt-3` to 1rem on Step 4,
  // while Step 3, whose <section> sets no space-y, shows the 0.75rem
  // `mt-3` alone. Wrapping makes the pair one child, so both screens
  // render identically - which is the whole point of sharing the
  // component.
  return (
    <div>
      <p className="text-body">
        Need to Step Away?{" "}
        <InlineLinkButton onClick={save} disabled={saving}>
          {saving
            ? "Preparing Configuration File..."
            : "Download Configuration File"}
        </InlineLinkButton>
      </p>
      <p className="text-body mt-3">
        This issuance request stays open for {SESSION_LIFETIME_HOURS} hours from
        the time it was created. Treat the configuration file like a password -
        anyone who has it can access this issuance request and its files.
      </p>
    </div>
  );
}

/**
 * The save-failure Alert, placed by the CALLER rather than by
 * StepAwaySave.
 *
 * WHY SEPARATE : on Step 4 the step-away line lives inside the
 * `status === "processing"` branch, but the failure it reports can
 * arrive after that branch has stopped rendering. A customer can press
 * Download Configuration File and then press Check Progress while the fetch
 * is still in flight - both controls are on screen together - and if
 * that check returns the signed certificate the screen moves to the
 * issued state. An Alert rendered inside the branch would have no
 * render site left, so the customer would have pressed a save control
 * and received neither a file nor a message. Callers therefore hold
 * the hook at component level and render this where it survives the
 * state change.
 *
 * Exported from the same module as the control so the two cannot drift
 * on the title or the dismiss behaviour.
 *
 * @param {string|null} error - message from useConfigFileSave
 * @param {function} onClose  - clearError from useConfigFileSave
 */
export function ConfigFileSaveError({ error, onClose }) {
  if (!error) return null;
  return (
    <Alert
      type="error"
      title="Unable to Save Configuration File"
      className="mb-4"
      live
      onClose={onClose}
    >
      {error}
    </Alert>
  );
}

export default StepAwaySave;
